import { useGetCustomGeoJsonList } from "@/api/hooks/customGeojson";
import ToggleSwitch from "@/components/atoms/ToggleSwitch/ToggleSwitch";
import { backgroundLayerConfig, customGeoJsonLayerConfig } from "@/constants/layerConfigAttributes";
import { FeatureHubGuard } from "@/featureFlags";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components";
import { AggregateSelect } from "@/overlay/components/CommonParts/AggregateSelect";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import {
  transformAdvancedThresholds,
  transformAdvancedThresholdsSchema,
  transformColorThresholds,
} from "@/overlay/components/CommonParts/Threshold/types";
import { Button } from "@/overlay/components/CommonParts/buttons";
import { GeoJSONManage } from "@/overlay/components/GeoJSONManage";
import { CustomGeoJsonFileSelect } from "@/overlay/components/LayerStackWindow/CustomGeoJsonForm/CustomGeoJsonFileSelect";
import { CustomGeoJsonLineResolutionOptions } from "@/overlay/components/LayerStackWindow/CustomGeoJsonForm/CustomGeoJsonLineResolutionOptions";
import { ColorPickerInput, OpacityInput } from "@/overlay/components/LayerStackWindow/LayerFormElements";
import { ModelAndWeatherParameterForm } from "@/overlay/components/ModelAndWeatherParameterForm";
import { FormField } from "@/overlay/components/form-field";
import { Modal } from "@/overlay/components/window/Modal";
import type { MapId } from "@/types/type-utils";
import { enumValues, toEnumValue } from "@/utility/enum";
import { getDefaultModelName } from "@/utility/layer";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Aggregate } from "@mm/api.meteomatics.com";
import type { CustomGeoJSONLayerOutput } from "@mm/metx-workbench.meteomatics.com";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import { parameterToString } from "@mm/weather-parameter-utils";
import type { DropdownItem } from "@mm/zeus";
import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { IntegerInput } from "../../CommonParts/CustomInput";
import type { DraggableItemIdentifier } from "../../CommonParts/SortableList";

export function CustomGeoJsonUpdateForm(props: {
  layer: CustomGeoJSONLayerOutput;
  mapId: MapId;
  onChange: (propsValue: Partial<CustomGeoJSONLayerOutput>, isGrouped?: boolean) => void;
}) {
  const desktop = useDesktop();
  const { isLoading, list } = useGetCustomGeoJsonList();
  const [showGeoJSONManageWindow, setShowGeoJSONManageWindow] = useState(false);

  const [segmentLength, setSegmentLength] = useState(
    props.layer.custom_options.segmentLength || customGeoJsonLayerConfig.defaultLineSegmentLength,
  );

  const handleAggregateSelected = useCallback(
    (items: DropdownItem[]) => {
      const singleItemId = items[0].id || Aggregate.max;
      const newAggregate = Object.values(Aggregate).includes(singleItemId as Aggregate)
        ? (singleItemId as Aggregate)
        : undefined;
      props.onChange({
        custom_options: {
          ...props.layer.custom_options,
          aggregate_value: newAggregate,
        },
      });
    },
    [props.layer.custom_options],
  );

  function handleAddParameter() {
    function submitNewParam(param: HasLayerIdxNarrowing<PartialWeatherParameter>, model: string | null) {
      const existing = props.layer.custom_options;

      // Ensure CustomGeoJSON defaults are present when a parameter gets added.
      // These are required by the UI (segment length/unit) and by aggregation selection.
      const customOptionsWithDefaults = {
        ...existing,
        segmentLength: existing.segmentLength ?? customGeoJsonLayerConfig.defaultLineSegmentLength,
        units: existing.units ?? customGeoJsonLayerConfig.defaultUnit,
        aggregate_value: existing.aggregate_value ?? (Aggregate.max as unknown as string),
      };

      props.onChange({
        ...props.layer,
        custom_options: customOptionsWithDefaults,
        model: model || getDefaultModelName(param),
        parameter_unit: parameterToString(param).formatted,
      });
    }

    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: instanceId(WindowKind.AddParameter),
      windowKind: WindowKind.AddParameter,
      customProps: {
        source: "add-param",
        onSubmitParam: (param, _parameterType, model) => submitNewParam(param, model),
      },
    });
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: instanceId(WindowKind.AddParameter),
      isVisible: true,
    });
  }

  const updateSegmentLength = useCallback(
    (segmentLength: number) => {
      props.onChange({
        custom_options: {
          ...props.layer.custom_options,
          segmentLength: segmentLength,
        },
      });
    },
    [props.layer.custom_options],
  );

  const debouncedHandleSegmentLengthUpdate = useMemo(() => debounce(updateSegmentLength, 500), [updateSegmentLength]);

  const handleSegmentLengthChange = (segmentLength: number) => {
    setSegmentLength(segmentLength);
    debouncedHandleSegmentLengthUpdate(segmentLength);
  };

  function handleRemoveParameterAndThreshold() {
    props.onChange({
      parameter_unit: undefined,
      model: undefined,
      thresholds: [],
      custom_options: {
        ...props.layer.custom_options,
        aggregate_value: Aggregate.max as unknown as string,
      },
    });
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!props.layer.thresholds) {
      console.error("Threshold orders were swapped but no data is available");
      return;
    }

    const copyOfThresholds = [...props.layer.thresholds];

    const fromIdx = dragStart.index;
    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }
    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    props.onChange({
      thresholds: copyOfThresholds,
    });
  }

  if (isLoading) {
    return <mm-loader size="large" />;
  }

  if (!list) {
    return <></>;
  }

  return (
    <>
      <CustomGeoJsonFileSelect
        selectedFileId={list.find((l) => l.user_file_id === props.layer.user_file_id)?.user_file_id}
        list={list}
        onChange={(user_file_id) =>
          props.onChange({
            ...props.layer,
            user_file_id: user_file_id,
          })
        }
      />

      <FeatureHubGuard flag="upload_window">
        <FormField label={"GeoJSON"}>
          <Button icon="folder_open" onClick={() => setShowGeoJSONManageWindow(true)}>
            <Trans>Manage files</Trans>
          </Button>
        </FormField>
        <Modal isOpen={showGeoJSONManageWindow} onClose={() => setShowGeoJSONManageWindow(false)}>
          <GeoJSONManage />
        </Modal>
      </FeatureHubGuard>

      <OpacityInput layer={props.layer} change={props.onChange} />
      <ColorPickerInput
        label={t`Color`}
        change={(color) =>
          props.onChange({
            ...props.layer,
            custom_options: {
              ...props.layer.custom_options,
              line_color: color,
            },
          })
        }
        value={props.layer.custom_options.line_color || backgroundLayerConfig.defaultLineColor}
      />
      <FormField label={t`Line Width`}>
        <IntegerInput
          {...customGeoJsonLayerConfig.lineWidthLimits}
          onChange={(value) => props.onChange({ custom_options: { ...props.layer.custom_options, line_width: value } })}
          value={props.layer.custom_options.line_width || customGeoJsonLayerConfig.defaultLineWidth}
          data-testid="line-width-input"
        />
      </FormField>
      <FeatureHubGuard flag="customgeojson_show_values">
        <FormField label={t`Show values`}>
          <ToggleSwitch
            checked={!!props.layer.custom_options.show_values}
            onChange={(enabled) =>
              props.onChange({
                custom_options: {
                  ...props.layer.custom_options,
                  show_values: enabled,
                },
              })
            }
          />
        </FormField>
        {props.layer.parameter_unit ? (
          <>
            <ColorPickerInput
              label={t`Value Color`}
              change={(color) =>
                props.onChange({
                  ...props.layer,
                  custom_options: {
                    ...props.layer.custom_options,
                    value_color: color,
                  },
                })
              }
              value={props.layer.custom_options.value_color || customGeoJsonLayerConfig.defaultValueColor}
            />
            <FormField label={t`Value Size`}>
              <IntegerInput
                {...customGeoJsonLayerConfig.valueTextSizeLimits}
                onChange={(value) =>
                  props.onChange({ custom_options: { ...props.layer.custom_options, value_text_size: value } })
                }
                value={props.layer.custom_options.value_text_size || customGeoJsonLayerConfig.defaultValueTextSize}
                data-testid="value-text-size-input"
              />
            </FormField>
          </>
        ) : null}
      </FeatureHubGuard>

      {!props.layer.parameter_unit ? (
        <FormField label={t`Parameter`}>
          <Button icon="add" onClick={handleAddParameter}>
            <Trans>Add Parameter</Trans>
          </Button>
        </FormField>
      ) : (
        <>
          <CustomGeoJsonLineResolutionOptions
            onSegmentChange={(segmentLength) => {
              handleSegmentLengthChange(segmentLength);
            }}
            unit={props.layer.custom_options.units ?? customGeoJsonLayerConfig.defaultUnit}
            segmentLength={segmentLength}
            onUnitChange={(unit) => {
              props.onChange({ custom_options: { ...props.layer.custom_options, units: unit } });
            }}
          />
          {props.layer.advanced_thresholds?.length ? null : (
            <>
              <ModelAndWeatherParameterForm
                layer={props.layer}
                mapId={props.mapId}
                onEditParameter={handleAddParameter}
                onRemoveParameter={handleRemoveParameterAndThreshold}
              />
              <AggregateSelect
                activeId={[
                  toEnumValue<Aggregate>(
                    props.layer.custom_options.aggregate_value || "",
                    enumValues(Aggregate),
                    Aggregate.max,
                  ),
                ]}
                change={(selectedItems) => handleAggregateSelected(selectedItems)}
              />
            </>
          )}

          <CompleteThreshold
            onMoveIndex={moveThresholdIdx}
            paramUnit={props.layer.parameter_unit}
            thresholds={transformColorThresholds(props.layer.thresholds)}
            advancedThresholds={transformAdvancedThresholds(props.layer.advanced_thresholds)}
            updateThresholds={(thresholds) => {
              props.onChange({
                thresholds,
              });
            }}
            updateAdvancedThresholds={(thresholds) => {
              props.onChange({
                advanced_thresholds: transformAdvancedThresholdsSchema(thresholds),
              });
            }}
            enableAdvancedThreshold
          />
        </>
      )}
    </>
  );
}
